import './back-card-design-tool.system-administrator.screen.scss';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {
  Avatar,
  Box,
  Step,
  StepLabel,
  Stepper,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Slider,
} from '@mui/material';
import {translations} from '../../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {useContext, useEffect, useRef, useState} from 'react';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {Entity} from '../../../../../../../backend/interfaces/entity';
import BusinessIcon from '@mui/icons-material/Business';
import ClearIcon from '@mui/icons-material/Clear';
import * as XLSX from 'xlsx';
import {ImagePaletteColors} from '../../../../../../../components/image-palette-colors/image-palette-colors';

const EventEmitter = {
  events: {},
  subscribe: function (event: any, callback: any) {
    // Check if the callbacks list for this event is already initialized or not
    if (!(this.events as any)[event]) {
      (this.events as any)[event] = [];
    }

    // Register callback
    (this.events as any)[event].push(callback);
  },
  dispatch: function (event: any, data?: any) {
    // If event has no subscribers, do nothing
    if (!(this.events as any)[event]) {
      return;
    }

    // Notify all subscribers
    (this.events as any)[event].forEach((callback: any) => {
      // Execute Callback with Data from the event dispatch
      callback(data);
    });
  },
};

// Constants
const templateWidth = 946;
const templateHeight = 344;
const safeAreaMargin = 31;
const textLineYStartPosition = 80;
const textGuideLineHeight = 178;
const safeAreaLogoMin = 73;
const safeAreaTextLineWidth = 25;
const safeAreaStrokeWidth = 2;
const safeAreaLogoMax = 663;
const safeAreaWidth = templateWidth - 2 * safeAreaMargin;
const safeAreaHeight = templateHeight - 2 * safeAreaMargin;
const textLineYEndPosition = textLineYStartPosition + textGuideLineHeight;
const textColor = '#161616';
const maxTextArea = 120;
const textMinPadding = 15;
const safeMaxLogoHeight = 155;
const safeMaxLogoBottom = 80;
const safeMaxLogoTop = templateHeight - safeMaxLogoBottom - safeMaxLogoHeight;

// Components
const CardPreview = (props: {
  canvasId: string;
  templateUrl: string;
  entityInfo: {
    entityName: string | undefined;
    entityNumber: string | undefined;
    seniorUsersCount: number | undefined;
    caregiverUsersCount: number | undefined;
    cardName: string | undefined;
    customEntityPicture:
      | {
          file: File;
          url: string;
          width: number;
          height: number;
        }
      | undefined;
    entityPictureHeight: number;
    entityNameTextColor: string;
    includeEntityName: boolean;
  };
  entityPicture: string | undefined;
  cardName: string | undefined;
}) => {
  // Hold Ref to canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // On mount
  useEffect(() => {
    // Quick check
    if (canvasRef.current) {
      // Draw
      drawCard(canvasRef.current, true);
    }

    // Subscribe to events
    EventEmitter.subscribe('exportCard', exportCard);

    return () => {
      // Unsubscribe
      EventEmitter.subscribe('exportCard', exportCard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On Props Change
  useEffect(() => {
    // EventEmitter.subscribe('exportCard', VerifyTextLimits);
  });

  // Calculate lines needed
  const lines = props.entityInfo.entityName?.split('\n').reverse();

  // Functions
  const exportCard = async () => {
    // Quick check
    if (!canvasRef.current) {
      return;
    }

    // Draw with no guidelines
    await drawCard(canvasRef.current, false);

    // Generate link
    let downloadLink = document.createElement('a');

    // Set attributes
    downloadLink.setAttribute('download', props.cardName ?? '');
    var dataUrl = canvasRef.current.toDataURL();
    let newURL = require('changedpi').changeDpiDataUrl(dataUrl, 300);

    // Set href
    downloadLink.setAttribute('href', newURL);

    // Trigger click
    downloadLink.click();
  };

  const drawSafeArea = (context: CanvasRenderingContext2D) => {
    context.beginPath();
    context.rect(0, 0, safeAreaMargin, templateHeight);
    context.fillStyle = '#FFFFFF';
    context.fill();

    context.beginPath();
    context.rect(0, 0, templateWidth, safeAreaMargin);
    context.fill();

    context.beginPath();
    context.rect(0, templateHeight - safeAreaMargin, templateWidth, templateHeight);
    context.fill();

    context.beginPath();
    context.rect(templateWidth - safeAreaMargin, 0, templateWidth, templateHeight);
    context.fill();
  };

  const drawGuidelines = (context: CanvasRenderingContext2D) => {
    // Draw Logo Min Line
    context.beginPath();
    context.strokeStyle = '#FF0000';
    context.lineWidth = safeAreaStrokeWidth;
    context.moveTo(safeAreaLogoMin, 0);
    context.lineTo(safeAreaLogoMin, templateHeight);
    context.stroke();

    // Draw Logo Max Line
    context.beginPath();
    context.strokeStyle = '#FF0000';
    context.lineWidth = safeAreaStrokeWidth;
    context.moveTo(safeAreaLogoMax, 0);
    context.lineTo(safeAreaLogoMax, templateHeight);
    context.stroke();

    context.strokeStyle = '#FF0000';
    context.lineWidth = safeAreaStrokeWidth;
    context.strokeRect(safeAreaMargin, safeAreaMargin, safeAreaWidth, safeAreaHeight);
  };

  const drawText = (
    context: CanvasRenderingContext2D,
    lines: Array<string> | undefined,
    textMinXPosition: number
  ) => {
    // Set fill
    context.fillStyle = textColor;

    // Quick check
    if (!lines) {
      return;
    }

    // Split Lines to Array
    let fontSize;
    if (lines.length <= 3) {
      //Normal Font size
      fontSize = 30;
    } else {
      //Calculate the font size using the size. 120 is the most are the IST Name Can Use
      fontSize = maxTextArea / (lines.length + 1);
    }

    // Set Font
    const textLineHeight = 1.5 * fontSize;
    context.font = `${fontSize}pt Montserrat`;

    // Padding and Positions
    const paddingBottom = textMinPadding + (safeMaxLogoHeight - textLineHeight * lines.length) / 2;
    const textMaxYPosition = safeMaxLogoTop + safeMaxLogoHeight - paddingBottom - 8;

    // For each line
    for (let i = lines.length - 1; i >= 0; i--) {
      // Draw line
      context.fillText(lines[i], textMinXPosition, textMaxYPosition - i * textLineHeight);
    }
  };

  const drawTextLine = (context: CanvasRenderingContext2D, textLineXPosition: number) => {
    // Draw Logo Min Line
    context.beginPath();
    context.strokeStyle = props.entityInfo.entityNameTextColor;
    context.lineWidth = 3;
    context.moveTo(textLineXPosition, textLineYStartPosition);
    context.lineTo(textLineXPosition, textLineYEndPosition);
    context.stroke();
  };

  const drawCard = (canvas: HTMLCanvasElement, drawLines: boolean) => {
    return new Promise<void>((resolve, reject) => {
      // Get Canvas Context to Draw over it
      const context = canvas.getContext('2d');

      // Quick check
      if (!context) {
        return;
      }

      // Draw Background
      context.beginPath();
      context.rect(0, 0, templateWidth, templateHeight);
      context.fillStyle = props.entityInfo.entityNameTextColor;
      context.fill();

      // Draw Safe Area
      drawSafeArea(context);

      // Draw image
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = props.templateUrl;

      image.onload = () => {
        // Draw Template
        context.drawImage(image, 0, 0, templateWidth, templateHeight);

        // Check entity picture
        if (!props.entityPicture) {
          return;
        }

        // Draw Logo
        const logo = new Image();
        logo.crossOrigin = 'anonymous';
        logo.src = props.entityInfo.customEntityPicture
          ? props.entityInfo.customEntityPicture.url
          : props.entityPicture;

        // On load
        logo.onload = () => {
          // Destination Height and Width
          const destinationHeight = props.entityInfo.entityPictureHeight;
          const destinationWidth = (destinationHeight * logo.width) / logo.height;

          // Calculate Y
          const logoYPosition =
            textLineYStartPosition + textGuideLineHeight / 2 - destinationHeight / 2;

          // Draw Logo
          context.drawImage(
            logo,
            safeAreaLogoMin,
            logoYPosition,
            destinationWidth,
            destinationHeight
          );

          // // Text Lines
          const textLineXPosition = safeAreaLogoMin + destinationWidth + safeAreaTextLineWidth;
          const textMinXPosition = textLineXPosition + safeAreaTextLineWidth;

          // Check if Draw Lines was required
          if (drawLines) {
            drawGuidelines(context);
          }

          // Draw Entity Name
          if (props.entityInfo.includeEntityName) {
            // Draw Text
            drawText(context, lines, textMinXPosition);

            // Draw Text Line
            drawTextLine(context, textLineXPosition);
          }

          //All Completed
          resolve();
        };
      };
    });
  };

  return (
    <div className="canvas-container">
      <canvas
        id={props.canvasId}
        className="cardCanvas"
        ref={canvasRef}
        width={templateWidth}
        height={templateHeight}
      />
    </div>
  );
};

export default function EntityBackCardDesignToolSystemAdministratorOfficeScreen() {
  // Translations
  const {t} = useTranslation();

  // Get context
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold State
  const [activeStep, setActiveStep] = useState(0);

  // Hold Ref to canvas
  const customEntityPictureInput = useRef<HTMLInputElement>(null);

  // Form
  const [entityName, setEntityName] = useState<string | undefined>(undefined);
  const [entityNumber, setEntityNumber] = useState<string | undefined>(undefined);
  const [seniorUsersCount, setSeniorUsersCount] = useState<number | undefined>(undefined);
  const [caregiverUsersCount, setCaregiverUsersCount] = useState<number | undefined>(undefined);
  const [cardName, setCardName] = useState<string | undefined>(undefined);
  const [customEntityPicture, setCustomEntityPicture] = useState<
    | {
        file: File;
        url: string;
        width: number;
        height: number;
      }
    | undefined
  >(undefined);
  const [entityPictureHeight, setEntityPictureHeight] = useState<number>(137);
  const [entityNameTextColor, setEntityNameTextColor] = useState<string>('');
  const [includeEntityName, setIncludeEntityName] = useState<boolean>(false);
  const [formHasErrors, setFormHasErrors] = useState<boolean>(false);

  // Functions
  function goToPreview() {
    // Validate form
    if (!entityName || entityName.trim().length === 0) {
      setFormHasErrors(true);
      return;
    }

    if (!entityNumber || entityNumber.trim().length === 0) {
      setFormHasErrors(true);
      return;
    }

    if (!seniorUsersCount || seniorUsersCount < 0) {
      setFormHasErrors(true);
      return;
    }

    if (!caregiverUsersCount || caregiverUsersCount < 0) {
      setFormHasErrors(true);
      return;
    }

    if (!cardName || cardName.trim().length === 0) {
      setFormHasErrors(true);
      return;
    }

    if (!entityNameTextColor || entityNameTextColor === '') {
      setFormHasErrors(true);
      return;
    }

    // No errors
    setFormHasErrors(false);

    // Go to preview
    setActiveStep(1);
  }

  function createSpreadsheet() {
    // Init data
    var data = [];

    // Functions
    const createCardId = (number: number) => {
      let cardId = '';
      if (number < 10) {
        cardId = `00${number}`;
      } else if (number >= 10) {
        cardId = `0${number}`;
      } else {
        cardId = `${number}`;
      }

      return cardId;
    };
    const s2ab = (s: any) => {
      var buf = new ArrayBuffer(s.length);
      var view = new Uint8Array(buf);
      for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };

    // For each senior user
    if (seniorUsersCount) {
      for (let i = 0; i < seniorUsersCount; i++) {
        let seniorUserCard = ['IST', entityNumber, `${cardName}_USR`, 'USR', createCardId(i + 1)];
        data.push(seniorUserCard);
      }
    }

    // For each caregiver user
    if (caregiverUsersCount) {
      for (let i = 0; i < caregiverUsersCount; i++) {
        let fncCard = ['IST', entityNumber, `${cardName}_FNC`, 'FNC', createCardId(i + 1)];
        data.push(fncCard);
      }
    }

    // Create workbook
    var wb = XLSX.utils.book_new();
    wb.Props = {
      Title: 'New Cards',
    };
    wb.SheetNames.push('Banco de Dados');

    // Append data
    var ws = XLSX.utils.aoa_to_sheet(data);
    wb.Sheets['Banco de Dados'] = ws;

    // Write file
    var wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'binary'});

    // Save file
    require('file-saver').saveAs(
      new Blob([s2ab(wbout)], {type: 'application/octet-stream'}),
      `${entityName}.Cards.xlsx`
    );
  }

  // On Page change
  useEffect(() => {
    if (activeStep === 1) {
      // Get users
      // generatePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Init steps
  const steps = [
    t(translations.entityBackCardDesignTool.step1),
    t(translations.entityBackCardDesignTool.step2),
  ];

  // Get current entity form
  const entity: Entity | undefined = officeContext.state.entityForm;
  if (!entity) return <>Entity Form not available</>;

  // Build
  return (
    <ContentPage>
      <h2>{t(translations.entityBackCardDesignTool.title)}</h2>
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{height: '16px'}}></Box>
      {activeStep === 0 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <TextField
              required={true}
              error={formHasErrors && (!entityName || entityName.trim().length === 0)}
              id="entity-name"
              label={t(translations.entityBackCardDesignTool.entityName)}
              variant="outlined"
              multiline={true}
              rows={3}
              value={entityName}
              onChange={(event) => {
                setEntityName(event.target.value);
              }}
            />
            <TextField
              required={true}
              error={formHasErrors && (!entityNumber || entityNumber.trim().length === 0)}
              id="entity-number"
              label={t(translations.entityBackCardDesignTool.entityNumber)}
              variant="outlined"
              value={entityNumber}
              onChange={(event) => {
                setEntityNumber(event.target.value);
              }}
            />
            <TextField
              required={true}
              error={formHasErrors && (!seniorUsersCount || seniorUsersCount < 0)}
              id="senior-users-count"
              label={t(translations.entityBackCardDesignTool.seniorUsersCount)}
              variant="outlined"
              type="number"
              value={seniorUsersCount}
              onChange={(event) => {
                setSeniorUsersCount(parseInt(event.target.value));
              }}
            />
            <TextField
              required={true}
              error={formHasErrors && (!caregiverUsersCount || caregiverUsersCount < 0)}
              id="caregiver-users-count"
              label={t(translations.entityBackCardDesignTool.caregiverUsersCount)}
              variant="outlined"
              type="number"
              value={caregiverUsersCount}
              onChange={(event) => {
                setCaregiverUsersCount(parseInt(event.target.value));
              }}
            />
            <TextField
              required={true}
              error={formHasErrors && (!cardName || cardName.trim().length === 0)}
              id="card-name"
              label={t(translations.entityBackCardDesignTool.cardName)}
              variant="outlined"
              value={cardName}
              onChange={(event) => {
                setCardName(event.target.value);
              }}
            />
            <h3>{t(translations.entityFrontCardDesignTool.entityPicture)}</h3>
            {!customEntityPicture && (
              <Avatar
                sx={{
                  width: '120px',
                  height: '120px',
                  maxWidth: '100%',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#FFFFFF',
                  borderRadius: '16px',
                }}
                alt={entity.name}
                src={entity.picture ?? ''}
              >
                <BusinessIcon
                  sx={{
                    fontSize: '100px',
                  }}
                />
              </Avatar>
            )}
            {customEntityPicture && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <img
                  id="custom-entity-picture"
                  className="custom-entity-picture"
                  alt="Entity Logo"
                  src={customEntityPicture.url}
                />
                <IconButton
                  aria-label="delete"
                  onClick={() => {
                    setCustomEntityPicture(undefined);
                    if (customEntityPictureInput && customEntityPictureInput.current) {
                      customEntityPictureInput.current.value = '';
                    }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            )}
            <input
              ref={customEntityPictureInput}
              type="file"
              accept="image/*"
              onChange={(e) => {
                e.preventDefault();

                // Evaluate
                if (e.target && e.target.files) {
                  // Get file
                  const file = e.target.files[0];

                  // Read file when completes loading
                  const reader = new FileReader();
                  reader.onloadend = (evt) => {
                    // Create and Read Image Dimensions
                    var img = new Image();

                    // On load
                    img.onload = () => {
                      //Done
                      setCustomEntityPicture({
                        file: file,
                        url: reader.result as string,
                        width: img.width,
                        height: img.height,
                      });
                    };

                    // Set src
                    img.src = reader.result as string;
                  };

                  // If file exists
                  if (file != null) {
                    // Read file
                    reader.readAsDataURL(file);
                  }
                }
              }}
            />
            <h4>{t(translations.entityBackCardDesignTool.entityPictureHeight)}</h4>
            <Box
              sx={{
                padding: '0 12px',
              }}
            >
              <Slider
                defaultValue={entityPictureHeight}
                value={entityPictureHeight}
                valueLabelDisplay="auto"
                min={138}
                max={178}
                onChange={(event: Event, newValue: number | number[]) => {
                  setEntityPictureHeight(newValue as number);
                }}
              />
            </Box>
            <ImagePaletteColors
              src={customEntityPicture ? customEntityPicture.url : (entity.picture ?? '')}
            >
              {({data, loading, error}) => (
                <>
                  {loading === false && data && (
                    <FormControl
                      sx={{
                        marginTop: '16px',
                      }}
                    >
                      <InputLabel id="entityNameTextColor-select-label">
                        {t(translations.entityFrontCardDesignTool.entityNameTextColor)}
                      </InputLabel>
                      <Select
                        required={true}
                        error={
                          formHasErrors && (!entityNameTextColor || entityNameTextColor === '')
                        }
                        labelId="entityNameTextColor-select-label"
                        id="entityNameTextColor-select"
                        value={entityNameTextColor}
                        label={t(translations.entityFrontCardDesignTool.entityNameTextColor)}
                        onChange={(event) => {
                          setEntityNameTextColor(event.target.value);
                        }}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              sx={{
                                marginRight: '16px',
                              }}
                              onClick={() => {
                                setEntityNameTextColor('');
                              }}
                              edge="end"
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        }
                      >
                        {Object.keys(data).map((key) => {
                          return (
                            <MenuItem key={`color-${key}`} value={data[key]}>
                              <Box sx={{display: 'flex'}}>
                                <Box
                                  sx={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    backgroundColor: data[key],
                                    marginRight: '16px',
                                  }}
                                ></Box>
                                <div>{key}</div>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </ImagePaletteColors>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeEntityName}
                  onChange={(event) => {
                    setIncludeEntityName(event.target.checked);
                  }}
                />
              }
              label={t(translations.entityBackCardDesignTool.includeEntityName)}
            />
          </Box>
          <Box sx={{paddingTop: '32px', textAlign: 'center'}}>
            <Button
              variant="contained"
              onClick={() => {
                goToPreview();
              }}
            >
              {t(translations.entityBackCardDesignTool.generatePreview)}
            </Button>
          </Box>
        </Box>
      )}
      {activeStep === 1 && (
        <Box>
          <Box className="preview">
            <CardPreview
              canvasId="USRCard"
              templateUrl={`${window.location.origin}/images/icon-mask_USR.png`}
              entityInfo={{
                entityName,
                entityNumber,
                seniorUsersCount,
                caregiverUsersCount,
                customEntityPicture,
                cardName,
                entityPictureHeight,
                entityNameTextColor,
                includeEntityName,
              }}
              entityPicture={entity.picture}
              cardName={`${cardName}_USR`}
            />
            <CardPreview
              canvasId="FNCCard"
              templateUrl={`${window.location.origin}/images/icon-mask_FNC.png`}
              entityInfo={{
                entityName,
                entityNumber,
                seniorUsersCount,
                caregiverUsersCount,
                customEntityPicture,
                cardName,
                entityPictureHeight,
                entityNameTextColor,
                includeEntityName,
              }}
              entityPicture={entity.picture}
              cardName={`${cardName}_FNC`}
            />
            {/* <DataCreator info={state} /> */}
          </Box>
          <Box
            sx={{
              marginTop: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                EventEmitter.dispatch('exportCard');
              }}
            >
              {t(translations.entityBackCardDesignTool.saveCards)}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                createSpreadsheet();
              }}
            >
              {t(translations.entityBackCardDesignTool.saveSpreadsheet)}
            </Button>
            <Button
              onClick={() => {
                setActiveStep(0);
              }}
            >
              {t(translations.entityBackCardDesignTool.restart)}
            </Button>
          </Box>
        </Box>
      )}
    </ContentPage>
  );
}
